import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { QUICKBOOKS_BRAND_COLOUR } from '@ankor-io/common/types/integrations/quickbooks'
import { XERO_BRAND_COLOR } from '@ankor-io/common/types/integrations/xero/types'
import { ToastProvider } from '@ankor-io/ui-components/src/ToastProvider'

import { ConfigProvider } from '@/providers/config'
import { ConnectedAccountProvider } from '@/providers/connectedAccount'
import { FeatureToggleProvider } from '@/providers/featureToggle'
import { GlobalLoadingProvider } from '@/providers/loading/GlobalLoadingProvider'
import QueryProvider from '@/providers/query/QueryProvider'
import { SCAProvider } from '@/providers/sca/SCAProvider'
import { DeferredRouter } from '@/routes/DeferredRouter'
import SCAModal from '@/views/components/SCAModal'

import './index.css'
import { KindeProvider } from './providers/kinde/KindeProvider'
import { UserContextProvider } from './providers/user/UserContextProvider'

document.documentElement.style.setProperty('--xero-branding', XERO_BRAND_COLOR)
document.documentElement.style.setProperty('--quickbooks-branding', QUICKBOOKS_BRAND_COLOUR)

// Defer Sentry initialization to not block React render
// This allows the logo to appear faster by not blocking on Sentry setup
const initSentry = () => {
  Sentry.init({
    dsn: 'https://fe4de24a22648103d31976e2112b482c@o4506936725536768.ingest.us.sentry.io/4509902395932672',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/pay.ankor.io\/api/,
      /^https:\/\/pay.ankor.dev\/api/,
      /^https:\/\/[a-zA-Z0-9-]+\.ankor\.dev\/api/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    // Enable logs to be sent to Sentry
    enableLogs: true,
  })
}

// Initialize Sentry after React renders to avoid blocking logo display
// Use requestIdleCallback if available, otherwise setTimeout as fallback
if (typeof requestIdleCallback !== 'undefined') {
  requestIdleCallback(initSentry, { timeout: 2000 })
} else {
  setTimeout(initSentry, 0)
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GlobalLoadingProvider>
      <QueryProvider>
        <ConfigProvider>
          <KindeProvider>
            <UserContextProvider>
              <FeatureToggleProvider>
                <ConnectedAccountProvider>
                  <SCAProvider>
                    <DeferredRouter />
                    <ToastProvider />
                    <SCAModal />
                  </SCAProvider>
                </ConnectedAccountProvider>
              </FeatureToggleProvider>
            </UserContextProvider>
          </KindeProvider>
        </ConfigProvider>
      </QueryProvider>
    </GlobalLoadingProvider>
  </StrictMode>,
)
